/* eslint max-lines: ["error", 270] */
/* eslint @typescript-eslint/ban-types: 0 */
import { useMutation } from '@apollo/client/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useState } from 'react';
import { usePropertyId } from '../../core/hooks/usePropertyId';
import useProperty from '../../property/hooks/useProperty';
import {
    cleaningListQuery,
    createCleaningQuery,
    updateCleaningQuery,
    useRemoveCleaningMutation,
} from '../queries/cleaningQueries';

const CleaningForm = ({
    cleaning,
    onClose,
    suggestedTime,
}: {
    onClose: () => void;
    suggestedTime?: string;
    cleaning?: {
        id: string;
        time: string;
        note: string;
        value: number;
        paid: boolean;
    };
}) => {
    const propertyId = usePropertyId();
    const { property, getCleaningConfig } = useProperty();
    const cleaningConfig = getCleaningConfig();
    const defaultFee = cleaningConfig?.fee ?? 125;
    const propertyCurrency = property?.currency ?? 'EUR';

    const [createCleaning] = useMutation(createCleaningQuery, {
        refetchQueries: [
            {
                query: cleaningListQuery,
                variables: { propertyId },
            },
        ],
    });
    const [updateCleaning] = useMutation(updateCleaningQuery, {
        refetchQueries: [
            {
                query: cleaningListQuery,
                variables: { propertyId },
            },
        ],
    });
    const [removeCleaning] = useRemoveCleaningMutation(propertyId);
    const [time, setTime] = useState(cleaning?.time ?? suggestedTime ?? '');
    const [note, setNote] = useState(cleaning?.note ?? '');
    const [value, setValue] = useState(cleaning?.value ?? defaultFee);
    const [paid, setPaid] = useState(cleaning?.paid ?? false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSave = async () => {
        if (!time.trim()) {
            setError('Time is required');
            return;
        }

        if (value <= 0) {
            setError('Value must be greater than 0');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            if (cleaning) {
                await updateCleaning({
                    variables: {
                        id: cleaning.id,
                        time: time.replace('T', ' '),
                        note,
                        value: +value,
                        paid,
                    },
                });
            } else {
                await createCleaning({
                    variables: {
                        propertyId,
                        time: time.replace('T', ' '),
                        note,
                        value: +value,
                        paid,
                    },
                });
            }
            onClose();
        } catch (err) {
            setError(
                err instanceof Error ? err.message : 'Failed to save cleaning'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!cleaning) return;

        setLoading(true);
        setError(null);

        try {
            await removeCleaning({
                variables: { id: cleaning.id },
            });
            onClose();
        } catch (err) {
            setError(
                err instanceof Error ? err.message : 'Failed to delete cleaning'
            );
            setLoading(false);
        }
    };

    return (
        <Dialog
            open
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 0,
                    border: '4px solid #6366f1',
                    backgroundColor: '#1a1a2e',
                },
            }}
        >
            <DialogTitle
                sx={{
                    borderBottom: '3px solid #6366f1',
                    backgroundColor: '#0f0f23',
                    fontWeight: 900,
                    fontSize: '1.5rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    py: 2.5,
                }}
            >
                {cleaning ? 'Edit Cleaning' : 'New Cleaning'}
            </DialogTitle>
            <DialogContent
                sx={{
                    backgroundColor: '#1a1a2e',
                    p: 0,
                }}
            >
                <Stack sx={{ py: 3, px: 3 }} spacing={2.5}>
                    {error && (
                        <Alert
                            severity="error"
                            onClose={() => setError(null)}
                            sx={{
                                borderRadius: 0,
                                border: '3px solid #ef4444',
                                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                fontWeight: 600,
                                '& .MuiAlert-icon': {
                                    color: '#ef4444',
                                },
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    <TextField
                        label="Time"
                        type="datetime-local"
                        value={time}
                        onChange={event => setTime(event.target.value)}
                        required
                        fullWidth
                        disabled={loading}
                        slotProps={{
                            inputLabel: {
                                shrink: true,
                            },
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 0,
                                '& input, & textarea': {
                                    backgroundColor: '#0f0f23',
                                },
                                '& fieldset': {
                                    borderWidth: '2px',
                                    borderColor: '#374151',
                                },
                                '&:hover fieldset': {
                                    borderWidth: '2px',
                                    borderColor: '#6366f1',
                                },
                                '&.Mui-focused fieldset': {
                                    borderWidth: '3px',
                                    borderColor: '#6366f1',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                fontSize: '1rem',
                                letterSpacing: '0.05em',
                            },
                        }}
                    />

                    <TextField
                        label="Note"
                        value={note}
                        onChange={event => setNote(event.target.value)}
                        fullWidth
                        disabled={loading}
                        placeholder="Add any relevant notes..."
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 0,
                                '& input, & textarea': {
                                    backgroundColor: '#0f0f23',
                                },
                                '& fieldset': {
                                    borderWidth: '2px',
                                    borderColor: '#374151',
                                },
                                '&:hover fieldset': {
                                    borderWidth: '2px',
                                    borderColor: '#6366f1',
                                },
                                '&.Mui-focused fieldset': {
                                    borderWidth: '3px',
                                    borderColor: '#6366f1',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                fontSize: '1rem',
                                letterSpacing: '0.05em',
                            },
                        }}
                    />

                    <TextField
                        label={`Value (${propertyCurrency})`}
                        type="number"
                        value={value}
                        onChange={event =>
                            setValue(Math.floor(Number(event.target.value)))
                        }
                        required
                        fullWidth
                        disabled={loading}
                        slotProps={{
                            htmlInput: {
                                min: 0,
                                step: 1,
                            },
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 0,
                                '& input, & textarea': {
                                    backgroundColor: '#0f0f23',
                                },
                                '& fieldset': {
                                    borderWidth: '2px',
                                    borderColor: '#374151',
                                },
                                '&:hover fieldset': {
                                    borderWidth: '2px',
                                    borderColor: '#6366f1',
                                },
                                '&.Mui-focused fieldset': {
                                    borderWidth: '3px',
                                    borderColor: '#6366f1',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                fontSize: '1rem',
                                letterSpacing: '0.05em',
                            },
                        }}
                    />

                    <Box
                        sx={{
                            border: '3px solid #22c55e',
                            p: 2,
                            backgroundColor: 'rgba(34, 197, 94, 0.1)',
                        }}
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={paid}
                                    onChange={event =>
                                        setPaid(event.target.checked)
                                    }
                                    disabled={loading}
                                    sx={{
                                        color: '#22c55e',
                                        '&.Mui-checked': {
                                            color: '#22c55e',
                                        },
                                    }}
                                />
                            }
                            label="Mark as Paid"
                            sx={{
                                '& .MuiFormControlLabel-label': {
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    fontSize: '1rem',
                                    color: '#22c55e',
                                },
                            }}
                        />
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions
                sx={{
                    borderTop: '3px solid #374151',
                    backgroundColor: '#0f0f23',
                    p: 3,
                    gap: 1.5,
                    justifyContent: 'flex-end',
                }}
            >
                <Button onClick={onClose} disabled={loading} variant="outlined">
                    Cancel
                </Button>
                {cleaning && (
                    <Button
                        color="error"
                        variant="outlined"
                        onClick={handleDelete}
                        disabled={loading}
                        startIcon={
                            loading ? <CircularProgress size={20} /> : null
                        }
                    >
                        Delete
                    </Button>
                )}
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CleaningForm;
